// Overview (NAV-002, `/`): the signed-in landing. For `billing:read` (super-admins hold it): a
// greeting, this month's figures, "Billed, last 12 months" with its table, "Needs your attention"
// with the quota tile under it (TASK-257) and the top customers. Installer-only principals see their signals; anyone else a short
// orientation. Each fetch runs only when the principal plausibly holds its permission, and a
// refused or failed one leaves its part out rather than showing a banner: a landing page full of
// error banners would defeat its purpose.
import { useContext, useEffect, useState } from "preact/hooks";
import { useApi } from "@nexus/ui";
import type { ApiClient } from "@nexus/ui";
import { ApiError } from "@nexus/ui";
import type { ScreenProps } from "../../router/route";
import { SessionContext } from "../../session/session";
import { BarChart } from "@nexus/ui";
import { useCustomerDirectory } from "../../ui/customer-directory";
import { Link } from "@nexus/ui";
import { addMonths, currentMonth } from "@nexus/ui";
import { Card, Chip, EmptyState, PageHeader, ShareBar, StatCard, StatGrid } from "@nexus/ui";
import { Loading } from "@nexus/ui";
import { Money } from "@nexus/ui";
import type { RevenueBreakdown, RevenueForecast } from "../revenue/model";
import { QuotaTile } from "./quota-tile";
import {
  attentionItems,
  billedBars,
  billedRows,
  formatCount,
  greeting,
  monthProgress,
  overviewKind,
  overviewStats,
  overviewSubtitle,
  topCustomers,
  type AttentionItem,
  type AuditEvent,
  type BillingPeriod,
  type CurSource,
  type InstallerSession,
} from "./model";

/** A permission-gated fetch: null (part absent) on any API error. */
function fetchOrNull<T>(promise: Promise<T>): Promise<T | null> {
  return promise.catch((error: unknown) => {
    if (error instanceof ApiError) return null;
    throw error;
  });
}

/** Most customers a landing page reads CUR sources for (one request each). */
const CUR_SCAN_LIMIT = 40;
/** At most this many of those requests at once, and only after the page's own reads have settled:
 *  a session may make 20 requests a second, and the rest of the app needs its share. */
const CUR_SCAN_CONCURRENCY = 2;

/** Every known customer's CUR sources, or null when none could be read. */
async function loadCurSources(client: ApiClient, orgIds: readonly string[], cancelled: () => boolean): Promise<CurSource[] | null> {
  const queue = orgIds.slice(0, CUR_SCAN_LIMIT);
  const pages: ({ items: CurSource[] } | null)[] = [];
  const worker = async () => {
    // Stops as soon as the page goes away (the landing redirect can leave Overview at once).
    for (let orgId = queue.shift(); orgId !== undefined && !cancelled(); orgId = queue.shift()) {
      pages.push(await fetchOrNull(client.get("/v1/admin/orgs/{orgId}/cur-sources", { path: { orgId } })));
    }
  };
  await Promise.all(Array.from({ length: CUR_SCAN_CONCURRENCY }, worker));
  const read = pages.filter((page) => page !== null);
  return read.length === 0 ? null : read.flatMap((page) => page.items);
}

const BILLED_SERIES = [{ id: "billed", label: "Billed", class: "series-bar" }] as const;

export function Home(_props: ScreenProps<"/">) {
  const client = useApi();
  const services = useContext(SessionContext);
  const session = services?.state.status === "signed-in" ? services.state.session : null;
  const directory = useCustomerDirectory();

  const [openPeriods, setOpenPeriods] = useState<readonly BillingPeriod[] | null>(null);
  const [installerSessions, setInstallerSessions] = useState<readonly InstallerSession[] | null>(null);
  const [recentAudit, setRecentAudit] = useState<readonly AuditEvent[] | null>(null);
  const [curSources, setCurSources] = useState<readonly CurSource[] | null>(null);
  const [byMonth, setByMonth] = useState<RevenueBreakdown | null>(null);
  const [byOrg, setByOrg] = useState<RevenueBreakdown | null>(null);
  const [forecast, setForecast] = useState<RevenueForecast | null>(null);
  const [loaded, setLoaded] = useState(false);

  const billing = session?.can("billing:read") ?? false;
  const installer = session?.can("installer:run") ?? false;

  useEffect(() => {
    if (!session) return;
    const now = new Date();
    const month = currentMonth(now);
    const tasks: Promise<unknown>[] = [];

    if (session.allows("super-admin")) {
      // Last month's still-open periods are the ones past due (the list is per month).
      tasks.push(
        fetchOrNull(client.get("/v1/admin/billing-periods", { query: { period: addMonths(month, -1), status: "open", limit: 500 } })).then((page) =>
          setOpenPeriods(page ? page.items : null),
        ),
      );
    }
    if (session.can("billing:read")) {
      tasks.push(
        fetchOrNull(client.get("/v1/admin/revenue/by-month", { query: { from: addMonths(month, -11), to: month, basis: "list" } })).then(setByMonth),
        fetchOrNull(client.get("/v1/admin/revenue/by-org", { query: { from: month, to: month, basis: "list" } })).then((result) => {
          setByOrg(result);
          if (result) directory.remember(result.items.map((item) => ({ id: item.key, name: item.label || item.key })));
        }),
        fetchOrNull(client.get("/v1/admin/revenue/forecast", {})).then(setForecast),
      );
    }
    if (session.can("installer:run")) {
      tasks.push(
        fetchOrNull(client.get("/v1/installer/sessions", { query: { state: "awaiting_approval" } })).then((page) =>
          setInstallerSessions(page ? page.items : null),
        ),
      );
    }
    tasks.push(fetchOrNull(client.get("/v1/admin/audit-events", { query: { limit: 10 } })).then((page) => setRecentAudit(page ? page.items : null)));

    void Promise.allSettled(tasks).then(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.me.subject]);

  // CUR freshness, once the customers are known (billing principals only).
  const directoryIds = directory.status === "ready" ? directory.entries.map((entry) => entry.id) : null;
  const directoryKey = directoryIds ? directoryIds.join(",") : "";
  useEffect(() => {
    if (!billing || directoryIds === null || !loaded) return;
    let cancelled = false;
    void loadCurSources(client, directoryIds, () => cancelled).then((sources) => {
      if (!cancelled) setCurSources(sources);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billing, directoryKey, loaded]);

  if (!loaded) return <Loading label="Loading the overview…" />;

  const now = new Date();
  const items = attentionItems({ now, openPeriods, curSources, installerSessions, recentAudit });
  const kind = overviewKind({ billing, installer }, items.length);
  const title = greeting(session?.me.name, now);

  if (kind === "orientation") {
    return (
      <div class="page overview-screen">
        <PageHeader title={title} lede="Nexus Admin runs billing and operations for the Bedrock proxy." />
        <EmptyState
          title="Nothing here needs you yet"
          lede="Your role does not include billing or installer work, so there is nothing to show on this page. The Help section explains what each part of Nexus does and which role sees it."
          actions={
            <Link href="/help/signing-in-and-roles" class="button button-primary">
              Read about roles in Help
            </Link>
          }
        />
      </div>
    );
  }

  if (kind === "signals") {
    return (
      <div class="page overview-screen">
        <PageHeader title={title} lede="What is waiting for you in the installer and the audit log." />
        <AttentionCard items={items} />
      </div>
    );
  }

  const progress = monthProgress(now);
  const stats = overviewStats({ now, byMonth, byOrg, forecast, knownCustomers: directory.status === "ready" ? directory.entries.length : null });
  const top = topCustomers(byOrg);
  const canOpenCustomers = session?.allows("super-admin") ?? false;
  const rows = billedRows(byMonth, now);

  return (
    <div class="page overview-screen">
      <PageHeader title={title} lede={overviewSubtitle(progress)} />

      {byMonth || byOrg ? (
        <StatGrid label="This month">
          <StatCard label="Billed this month" help="finance.billedThisMonth" value={<Money micro={stats.billed} />} note={stats.billedNote} />
          <StatCard label="Gross profit this month" help="common.grossProfit" accent value={<Money micro={stats.grossProfit} />} note={stats.grossProfitNote} />
          <StatCard
            label="Active customers"
            help="finance.activeCustomers"
            value={
              <>
                {stats.activeCustomers}
                {stats.knownCustomers !== null && <span class="stat-of"> of {stats.knownCustomers}</span>}
              </>
            }
            note={stats.activeNote}
          />
          <StatCard label="Tokens this month" help="finance.tokensThisMonth" value={formatCount(stats.tokens)} note={stats.tokensNote} />
        </StatGrid>
      ) : null}

      <div class="split">
        <Card
          title="Billed, last 12 months"
          actions={
            <Link href="/revenue" class="button-link">
              Open Revenue &amp; margin
            </Link>
          }
        >
          {byMonth ? (
            <>
              <BarChart
                bars={billedBars(byMonth, now)}
                series={BILLED_SERIES}
                legend={false}
                height={200}
                label="Billed to customers each month, last 12 months"
                caption="Hover a bar for the exact figure. This month, still running, is lighter."
              />
              <details class="chart-table">
                <summary>Show the numbers</summary>
                <div class="table-scroll">
                  <table class="data-table">
                    <caption class="visually-hidden">Billed and profit per month, last 12 months</caption>
                    <thead>
                      <tr>
                        <th scope="col">Month</th>
                        <th scope="col" class="numeric">
                          Billed
                        </th>
                        <th scope="col" class="numeric">
                          Profit
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={row.month}>
                          <td>
                            {row.label}
                            {row.running && <span class="cell-sub">so far</span>}
                          </td>
                          <td class="numeric">
                            <Money micro={row.billed} />
                          </td>
                          <td class="numeric cell-profit">
                            <Money micro={row.profit} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </>
          ) : (
            <p class="muted">The revenue report could not be read.</p>
          )}
        </Card>

        <div class="stack">
          <AttentionCard items={items} />
          <QuotaTile />
        </div>
      </div>

      <Card
        title="Top customers this month"
        actions={
          canOpenCustomers ? (
            <Link href="/customers" class="button-link">
              All customers
            </Link>
          ) : (
            <Link href="/revenue" class="button-link">
              All customers in Revenue
            </Link>
          )
        }
      >
        {top.length === 0 ? (
          <p class="muted">No customer has been billed this month yet.</p>
        ) : (
          <ol class="top-customers">
            {top.map((customer) => (
              <li key={customer.id}>
                {canOpenCustomers ? (
                  <Link href={`/customers/${encodeURIComponent(customer.id)}/usage`} class="top-customer-name">
                    {customer.name}
                  </Link>
                ) : (
                  <span class="top-customer-name">{customer.name}</span>
                )}
                <span class="top-customer-amount">
                  <Money micro={customer.billed} />
                </span>
                <ShareBar fraction={customer.share} label={`${Math.round(customer.share * 100)}% of this month's billed`} />
              </li>
            ))}
          </ol>
        )}
      </Card>
    </div>
  );
}

function AttentionCard({ items }: { items: readonly AttentionItem[] }) {
  return (
    <Card title="Needs your attention">
      {items.length === 0 ? (
        <p class="muted">Nothing needs your attention right now.</p>
      ) : (
        <ul class="attention-list">
          {items.map((item) => (
            <li key={item.key}>
              <Link href={item.href} class="attention-item">
                <span class="attention-text">
                  <span class="attention-title">{item.title}</span>
                  <span class="attention-detail">{item.detail}</span>
                </span>
                <Chip tone={item.chip.tone}>{item.chip.label}</Chip>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
